import { BrowserModule, Title } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, inject, provideAppInitializer } from '@angular/core';
import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { AuthenticationService } from '@charlesschwab/sac-ui-core';
import { AppSettingsService, TokenHttpInterceptor, SpinnerModule } from '@charlesschwab/as-ui-core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppService } from './app.service';
import { AppComponent } from './app.component';
import { AgGridModule } from 'ag-grid-angular';
import { MutualFundInvestingComponent } from './mutual-fund-investing/mutual-fund-investing.component';
import { ViewInstructionsComponent } from './mutual-fund-investing/view-instructions/view-instructions.component';
// import custom components used for aggrid
import { CheckboxRendererComponent } from './mutual-fund-investing/view-instructions/components/checkbox-renderer.component';
import { MutualFundInvestingService } from './mutual-fund-investing/mutual-fund-investing.service';
import { TokenService } from './shared/services/token.service';
import { IntegrationService } from './shared/services/integration.service';
import { SacAipIntent } from './sac-aip.intent';
import { AipInterceptor } from './shared/interceptors/aip.interceptor';
import { CreateEditInstructionsComponent } from './mutual-fund-investing/create-edit-instructions/create-edit-instructions.component';
import { PreviewInstructionsComponent } from './mutual-fund-investing/preview-instructions/preview-instructions.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AccountSearchModule } from './shared/components/account-search/account-search.module';



import { InstructionNotificationComponent } from './shared/components/instruction-notification/instruction-notification.component';
import { ViewEditRendererComponent } from './mutual-fund-investing/view-instructions/components/view-edit-renderer.component';
import { InstructionActionsComponent } from './mutual-fund-investing/instruction-actions/instruction-actions-component';
import {
  NumberFloatingFilterComponent,
  DateFloatingFilterComponent,
  TextFloatingFilterComponent,
  AccountCellRendererComponent,
  FrequencyFloatingFilterComponent,
  DayToExecuteFilterComponent,
  SymbolFilterComponent,
  AccountFilterComponent,
  TransactionFilterComponent,
  AmountTypeFilterComponent,
  InstructionStatusFilterComponent
} from './mutual-fund-investing/view-instructions/components/index';
import { OverlayModule } from '@angular/cdk/overlay';
import { StorageModule } from './shared/storage/storage-module';
import { InformationMessageTooltipComponent } from './mutual-fund-investing/view-instructions/components/information-message/information-message-tooltip.component';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PaginationBarComponent } from './mutual-fund-investing/view-instructions/components/pagination-bar/pagination-bar.component';
import { CommonTooltipWithIconImageComponent } from './mutual-fund-investing/view-instructions/components/common-tooltip-with-icon-image/common-tooltip-with-icon-image.component';
import { NoResultMessageComponent } from './shared/components/no-result-message/no-result-message.component';
import { CustomLoadingOverlayComponent } from './shared/components/custom-loading-overlay/custom-loading-overlay.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { dateFormat } from './shared/constants/dateFormat';
import { FEATURE_TOGGLE_CONFIGURATION_PROPERTIES, FeatureToggleConfigurationProperties, SchwabFeatureToggleAngularModule } from '@schwab/schwab-feature-toggle-angular';
import { FeatureFlagService } from './shared/services/feature-flag.service';

export function initializeAppFactory(appService: AppService) {
  return (): Promise<any> => {
    return appService.initializeApp();
  };
}

export function initializeFeatureFlagConfig(appService : AppService) : FeatureToggleConfigurationProperties{
  return appService.setClientSideId();
}

export function initializeFeatureFlagFactory(featureFlagService: FeatureFlagService) {
  return (): Promise<any> => {
    return featureFlagService.initializeFeatureFlags();
  };
}

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [BrowserModule,
    CommonModule,
    FormsModule,
    SpinnerModule,
    AppRoutingModule,
    AgGridModule,
    ReactiveFormsModule,
    AccountSearchModule,
    OverlayModule,
    StorageModule,
    MatSelectModule,
    MatTooltipModule,
    BrowserAnimationsModule,
    MatDatepickerModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    SchwabFeatureToggleAngularModule.forRoot(), MutualFundInvestingComponent,
    ViewInstructionsComponent,
    CheckboxRendererComponent,
    CreateEditInstructionsComponent,
    PreviewInstructionsComponent,
    InstructionNotificationComponent,
    ViewEditRendererComponent,
    InstructionActionsComponent,
    NumberFloatingFilterComponent,
    DateFloatingFilterComponent,
    TextFloatingFilterComponent,
    AccountCellRendererComponent,
    FrequencyFloatingFilterComponent,
    DayToExecuteFilterComponent,
    AccountFilterComponent,
    InformationMessageTooltipComponent,
    PaginationBarComponent,
    SymbolFilterComponent,
    InformationMessageTooltipComponent,
    CommonTooltipWithIconImageComponent,
    NoResultMessageComponent,
    CustomLoadingOverlayComponent,
    TransactionFilterComponent,
    AmountTypeFilterComponent,
    InstructionStatusFilterComponent], providers: [
        AppSettingsService,
        Title,
        AuthenticationService,
        AppService,
        IntegrationService,
        TokenService,
        MutualFundInvestingService,
        SacAipIntent,
        { provide: APP_BASE_HREF, useValue: '/' },
        { provide: HTTP_INTERCEPTORS, useClass: AipInterceptor, multi: true },
        provideAppInitializer(() => {
            const initializerFn = (initializeAppFactory)(inject(AppService));
            return initializerFn();
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenHttpInterceptor,
            multi: true,
            deps: [AuthenticationService]
        },
        provideMomentDateAdapter(dateFormat),
        provideAppInitializer(() => {
            const initializerFn = (initializeFeatureFlagFactory)(inject(FeatureFlagService));
            return initializerFn();
        }),
        {
            provide: FEATURE_TOGGLE_CONFIGURATION_PROPERTIES,
            useFactory: initializeFeatureFlagConfig,
            deps: [AppService]
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {}
